// drawer behaviour — backs itemsList's filters / settings / export panels.
//
// Contract: drawer.get(el) -> { show, hide, toggle, destroy } | null (see base
// scripts/behaviors.contract.md). base itemsList.js toggleAside() resolves each panel
// by id (<listId>_<position>_window), calls toggle() on the requested one and hide()
// on the rest.
//
// cleanSlate has no drawer framework, so the adapter is a thin state toggle: it only
// flips data-zit-drawer-open (+ aria-hidden) on the panel. Whether an open drawer
// reads as a pinned aside (wide screens: <main> reserves a gutter) or as an overlay
// over a scrim (narrow screens) is decided entirely in style/index.css, so there is
// no viewport logic here — the "adapter chooses the layout mode" clause of the
// contract is satisfied by the stylesheet it ships with.
//
// Dismissal: any [data-zit-drawer-dismiss] inside the panel, the Escape key, and a
// click on the shared scrim (a single body-level element, visible only below lg).

// Every drawer instance that is currently open, so the shared scrim can close them.
const openInstances = new Set();

// The shared body-level scrim, created lazily on the first show().
let scrimEl = null;

// Create (once) the scrim; a click on it closes every open drawer. Its visibility is
// CSS-driven (body:has(an open drawer), below lg only), so it is never toggled here.
function ensureScrim() {
    if (scrimEl !== null) {
        return scrimEl;
    }

    scrimEl = document.createElement('div');
    scrimEl.className = 'zit-cs-drawer-scrim';
    scrimEl.setAttribute('aria-hidden', 'true');
    scrimEl.addEventListener('click', function () {
        // copy first: hide() mutates the set while we iterate
        Array.from(openInstances).forEach((instance) => instance.hide());
    });
    document.body.appendChild(scrimEl);

    return scrimEl;
}

// Build (once) the drawer instance for a panel element, cached on the element (the
// submenu.js pattern) so repeated get() calls share one state machine.
function ensureDrawerInstance(drawerEl) {
    if (drawerEl.zitDrawerInstance) {
        return drawerEl.zitDrawerInstance;
    }

    // Escape closes this drawer while it is open.
    const onKeydown = function (e) {
        if (e.key === 'Escape') {
            instance.hide();
        }
    };

    // A dismiss control (header close, footer cancel) closes the drawer; it never
    // submits or navigates, even if it is a submit button or a link.
    const onDismissClick = function (e) {
        e.preventDefault();
        instance.hide();
    };

    // The dismiss controls present in the panel markup at first get().
    const dismissEls = Array.from(drawerEl.querySelectorAll('[data-zit-drawer-dismiss]'));

    const instance = {
        // Whether the panel currently carries the open state.
        isShown: function () {
            return drawerEl.dataset.zitDrawerOpen === 'true';
        },
        // Open the panel. Idempotent: an already-open drawer is left untouched, so the
        // CSS transition does not replay.
        show: function () {
            if (instance.isShown()) {
                return;
            }
            ensureScrim();
            drawerEl.dataset.zitDrawerOpen = 'true';
            drawerEl.setAttribute('aria-hidden', 'false');
            openInstances.add(instance);
            document.addEventListener('keydown', onKeydown);
        },
        // Close the panel. Idempotent.
        hide: function () {
            if (!instance.isShown()) {
                return;
            }
            drawerEl.dataset.zitDrawerOpen = 'false';
            drawerEl.setAttribute('aria-hidden', 'true');
            openInstances.delete(instance);
            document.removeEventListener('keydown', onKeydown);
        },
        toggle: function () {
            if (instance.isShown()) {
                instance.hide();
            } else {
                instance.show();
            }
        },
        // Close the panel and release every listener; a later get() rebuilds it.
        destroy: function () {
            instance.hide();
            dismissEls.forEach((el) => el.removeEventListener('click', onDismissClick));
            delete drawerEl.zitDrawerInstance;
        }
    };

    dismissEls.forEach((el) => el.addEventListener('click', onDismissClick));

    drawerEl.zitDrawerInstance = instance;
    return instance;
}

const drawer = {
    // Resolve the instance for a drawer panel element; null when there is no element.
    get: function (el) {
        if (!el) {
            return null;
        }

        return ensureDrawerInstance(el);
    }
};

export default drawer;
